import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaExternalLinkAlt, FaRedo, FaTimes } from 'react-icons/fa';
import { useT } from '../../hooks/useT';
import { acquireModalLock } from '../../utils/modalLock';
import { clearDemoState } from '../../utils/demoRegistry';
import styles from './DemoOverlay.module.css';

function DemoOverlay({ demo, title, onClose }) {
  const t = useT();
  const [loaded, setLoaded] = useState(false);
  const [generation, setGeneration] = useState(0);
  const closeRef = useRef(null);

  useEffect(() => acquireModalLock(), []);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const reset = () => {
    clearDemoState(demo);
    setLoaded(false);
    setGeneration((g) => g + 1);
  };

  return createPortal(
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
      <div className={styles.window}>
        <div className={styles.toolbar}>
          <span className={styles.dots} aria-hidden="true"><i /><i /><i /></span>
          <span className={styles.address}>{window.location.host}{demo.path}</span>
          <span className={styles.badge}>{t('demo.badge')}</span>
          <button type="button" className={styles.action} onClick={reset} aria-label={t('demo.reset')}>
            <FaRedo aria-hidden="true" />
            <span className={styles.actionLabel}>{t('demo.reset')}</span>
          </button>
          <a className={styles.action} href={demo.path} target="_blank" rel="noopener noreferrer" aria-label={t('demo.openNewTab')}>
            <FaExternalLinkAlt aria-hidden="true" />
            <span className={styles.actionLabel}>{t('demo.openNewTab')}</span>
          </a>
          <button ref={closeRef} type="button" className={styles.action} onClick={onClose} aria-label={t('demo.close')}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        <div className={styles.viewport}>
          {!loaded && <div className={styles.loading} role="status">{t('demo.loading')}</div>}
          <iframe
            key={generation}
            className={styles.frame}
            src={demo.path}
            title={title}
            allow="clipboard-write"
            onLoad={() => setLoaded(true)}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DemoOverlay;
